class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.predictiveSearchEnabled = PredictiveSearch.isSupported();
    this.fallbackCache = {};

    this.setupEventListeners();
  }

  static isSupported() {
    try {
      const features = document.getElementById('shopify-features');
      return JSON.parse(features?.textContent || '{}').predictiveSearch === true;
    } catch (e) {
      return false;
    }
  }

  get isOverlayMode() {
    return this.hasAttribute('data-overlay-mode');
  }

  get searchOverlay() {
    return document.getElementById('SearchOverlay');
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.isOverlayMode) {
      this.input.addEventListener('input', () => {
        this.searchOverlay?.onOverlayInput?.(this.input.value);
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.isOverlayMode) {
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (this.isOverlayMode) {
      this.searchOverlay?.onOverlayInput?.(newSearchTerm);
      if (!this.searchTerm.length) {
        this.closeResults(true);
        return;
      }
      this.getSearchResults(this.searchTerm);
      return;
    }

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[data-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    if (this.isOverlayMode) {
      this.searchOverlay?.onSearchSubmit?.(this.getQuery());
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      if (this.isOverlayMode) {
        this.searchOverlay?.onOverlayInput?.('');
      }
    }
  }

  onFocus() {
    if (this.isOverlayMode) {
      if (!this.searchOverlay?.hasAttribute('open')) {
        this.searchOverlay?.open(this.input);
      }
      return;
    }

    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (this.isOverlayMode) return;

    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (this.isOverlayMode) {
      if (!this.getQuery().length) {
        this.closeResults(true);
        this.searchOverlay?.onOverlayInput?.('');
      }

      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up');
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
      return;
    }

    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open') && !this.isOverlayMode) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[data-selected="true"]');
    const scope = this.isOverlayMode ? this.searchOverlay : this;

    const allVisibleElements = Array.from(
      scope.querySelectorAll(
        '.search-overlay-articles__link, .search-overlay-products--slider .card__heading a, button.predictive-search__item'
      )
    ).filter((element) => element.offsetParent !== null);

    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('data-selected', true);
    if (selectedElement) selectedElement.setAttribute('data-selected', false);
  }

  selectOption() {
    const selectedOption = document.querySelector('[data-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getFallbackSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.fallbackCache[queryKey]) {
      this.renderSearchResults(this.fallbackCache[queryKey]);
      return;
    }

    fetch(
      `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last&type=product,article,page&section_id=search-suggest`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-search-suggest');
        const resultsMarkup = section ? section.innerHTML : '';

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.fallbackCache[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  getSearchResults(searchTerm) {
    if (!this.predictiveSearchEnabled) {
      this.getFallbackSearchResults(searchTerm);
      return;
    }

    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      const cached = this.cachedResults[queryKey];
      if (this.isOverlayMode) {
        this.renderSearchResults(cached.markup, cached.searchCount);
      } else {
        this.renderSearchResults(cached);
      }
      return;
    }

    const sectionId = this.isOverlayMode ? 'predictive-search-overlay' : 'predictive-search';

    const predictiveFetch = fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=${sectionId}`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const sectionSelector = this.isOverlayMode
          ? '#shopify-section-predictive-search-overlay'
          : '#shopify-section-predictive-search';
        return new DOMParser().parseFromString(text, 'text/html').querySelector(sectionSelector).innerHTML;
      });

    if (!this.isOverlayMode) {
      predictiveFetch
        .then((resultsMarkup) => {
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          });
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          if (error?.code === 20) {
            return;
          }
          this.close();
          throw error;
        });
      return;
    }

    const searchCountFetch = fetch(
      `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last&section_id=search-results-count`,
      { signal: this.abortController.signal }
    )
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => {
        if (!text) return null;

        const countNode = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-search-results-count [data-search-results-count]');

        const searchCount = Number(countNode?.dataset.searchResultsCount);
        return Number.isNaN(searchCount) ? null : searchCount;
      })
      .catch(() => null);

    Promise.all([predictiveFetch, searchCountFetch])
      .then(([resultsMarkup, searchCount]) => {
        const cached = { markup: resultsMarkup, searchCount };
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = cached;
        });
        this.renderSearchResults(resultsMarkup, searchCount);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);

    if (this.isOverlayMode) {
      this.searchOverlay?.querySelector('.search-overlay__main')?.classList.add('is-loading');
    }
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, searchCount = null) {
    if (this.isOverlayMode) {
      this.searchOverlay?.mountResults?.(resultsMarkup, searchCount);
      this.setAttribute('results', true);
      this.setLiveRegionResults();
      this.open();
      return;
    }

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    if (this.isOverlayMode) {
      this.searchOverlay?.querySelector('.search-overlay__main')?.classList.remove('is-loading');
    }

    const countRoot = this.isOverlayMode ? this.searchOverlay?.querySelector('[data-search-results-slot]') : this;
    const countNode = countRoot?.querySelector('[data-predictive-search-live-region-count-value]');
    if (countNode) {
      this.setLiveRegionText(countNode.textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (this.isOverlayMode) {
      this.setAttribute('open', true);
      this.isOpen = true;
      return;
    }

    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      if (this.isOverlayMode) {
        this.searchOverlay?.mountResults?.('');
        this.searchOverlay?.updateResultsVisibility?.('');
      }
    }

    const selected = document.querySelector('[data-selected="true"]');
    if (selected) selected.setAttribute('data-selected', false);

    this.removeAttribute('loading');
    this.removeAttribute('open');

    if (this.isOverlayMode) {
      this.searchOverlay?.querySelector('.search-overlay__main')?.classList.remove('is-loading');
    }

    if (!this.isOverlayMode) {
      this.resultsMaxHeight = false;
      this.predictiveSearchResults?.removeAttribute('style');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
